import { AddToListMode } from "@/enum/list.enum";

import type { TFunction } from "i18next";

/**
 * What `POST list/bulkAddToAnotherList` answered, in distinct people: those it added, those the
 * destination already held, and the picked rows it dropped. Typed loosely on purpose: a count
 * the answer lacks is unknown, and is never read as zero.
 */
export interface TransferResult {
	added?: unknown;
	skipped?: unknown;
	notTransferred?: unknown;
}

export interface TransferMessageInput {
	mode: AddToListMode;
	listName: string;
	/** The destination was created by this request. */
	isNewList?: boolean;
	/** What the selection counted; a company selection adds the companies' contacts. */
	selectionUnit?: SelectionUnit;
	/** The add queued work that is still running (Contacts → Add to List queues lead scoring). */
	processingNote?: boolean;
	result: TransferResult;
}

export type SelectionUnit = "contacts" | "companies";

const KEY = "contacts.transferResult";

/** A count the answer holds, or null when it is missing or not a number. */
export const knownCount = (value: unknown): number | null => {
	if (typeof value !== "number" && typeof value !== "string") {
		return null;
	}

	const count = Number(value);

	return value !== "" && Number.isFinite(count) ? count : null;
};

const headline = (
	t: TFunction,
	{ mode, listName, isNewList }: TransferMessageInput,
	added: number | null
) => {
	const isMove = mode === AddToListMode.MOVE;

	if (added === null) {
		return t(isMove ? `${KEY}.movedUnknown` : `${KEY}.addedUnknown`, { name: listName });
	}

	if (added === 0) {
		return t(isNewList ? `${KEY}.createdEmpty` : `${KEY}.noneAdded`, { name: listName });
	}

	return t(isMove ? `${KEY}.moved` : `${KEY}.added`, { count: added, name: listName });
};

/**
 * The message shown after "Add to List" on a list, built from the server's answer. It used to name
 * the selection's size ("Adding 1016 contacts"), while the server skips every lead the destination
 * already holds in any tab, so a list grew by 613 and the user reported lost leads (PREC-4595).
 * A move removes every transferred lead from this list, the skipped ones included, so it says so.
 */
export const transferResultMessage = (t: TFunction, input: TransferMessageInput): string => {
	const isMove = input.mode === AddToListMode.MOVE;
	const added = knownCount(input.result.added);
	const skipped = added === null ? null : knownCount(input.result.skipped);
	const notTransferred = knownCount(input.result.notTransferred);

	const sentences = [headline(t, input, added)];

	if (skipped) {
		sentences.push(
			t(isMove ? `${KEY}.alreadyThereRemoved` : `${KEY}.alreadyThere`, { count: skipped })
		);
	}

	if (notTransferred) {
		sentences.push(t(isMove ? `${KEY}.notMoved` : `${KEY}.notAdded`, { count: notTransferred }));
	}

	if (added === 0 && !skipped && input.selectionUnit === "companies") {
		sentences.push(t(`${KEY}.companiesWithoutContacts`));
	}

	if (input.processingNote && added !== 0) {
		sentences.push(t(`${KEY}.processingNote`));
	}

	return sentences.join(" ");
};
